import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import UserProfileModal from './components/UserProfileModal';
import WorkoutDashboard from './components/WorkoutDashboard';
import HistoryModal from './components/HistoryModal';
import RadioModal from './components/RadioModal';
import { generateWorkoutProgram } from './utils/workoutCalculator';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { RadioProvider } from './context/RadioContext';
import { useWorkoutHistory } from './hooks/useWorkoutHistory';
import './App.css';

const AppContent = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [workoutProgram, setWorkoutProgram] = useState(null);
  const [selectedIntensity, setSelectedIntensity] = useState('mediu');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { history, stats, clearHistory } = useWorkoutHistory();

  // Validare strictă a profilului
  const isProfileComplete = (profile) => {
    if (!profile) return false;
    return (
      profile.firstName && 
      profile.firstName.trim() !== '' &&
      profile.sex &&
      profile.age &&
      profile.height_cm &&
      profile.weight_kg &&
      profile.fitnessLevel
    );
  };

  useEffect(() => {
    try {
      const savedProfile = localStorage.getItem('userProfile');
      
      if (savedProfile) {
        const profile = JSON.parse(savedProfile);
        if (isProfileComplete(profile)) {
          setUserProfile(profile);
          const program = generateWorkoutProgram(profile, selectedIntensity);
          setWorkoutProgram(program);
        } else {
          navigate('/profile');
        }
      } else {
        navigate('/profile');
      }
    } catch (e) {
      console.error("Eroare la încărcarea profilului:", e);
      localStorage.removeItem('userProfile');
      navigate('/profile');
    }
  }, []);

  useEffect(() => {
    if (!userProfile && location.pathname !== '/profile') {
      navigate('/profile', { replace: true });
    }
  }, [userProfile, location.pathname, navigate]);

  const handleProfileSubmit = (profile) => {
    setUserProfile(profile);
    localStorage.setItem('userProfile', JSON.stringify(profile));
    const program = generateWorkoutProgram(profile, selectedIntensity);
    setWorkoutProgram(program);
    navigate('/');
  };

  const handleIntensityChange = (intensity) => {
    setSelectedIntensity(intensity);
    if (userProfile) {
      const program = generateWorkoutProgram(userProfile, intensity);
      setWorkoutProgram(program);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-blue-100 relative">
      
      {/* Dashboard in background */}
      {userProfile && workoutProgram && (
        <div className={location.pathname !== '/' ? 'blur-sm scale-[0.99] transition-all duration-300 pointer-events-none' : 'transition-all duration-300'}>
          <WorkoutDashboard 
            userProfile={userProfile} 
            workoutProgram={workoutProgram} 
            selectedIntensity={selectedIntensity}
            onIntensityChange={handleIntensityChange}
          />
        </div>
      )}

      {/* Routes for Modals */}
      <Routes>
        <Route path="/profile" element={
          <UserProfileModal 
            onSubmit={handleProfileSubmit} 
            existingProfile={userProfile}
            onClose={() => userProfile ? navigate('/') : null}
          />
        } />
        
        <Route path="/history" element={
          <HistoryModal 
            history={history} 
            stats={stats} 
            onClear={clearHistory}
            onClose={() => navigate('/')}
          />
        } />

        <Route path="/radio" element={
          <RadioModal 
            onClose={() => navigate('/')}
          />
        } />
        
        <Route path="*" element={userProfile ? <Navigate to="/" /> : <Navigate to="/profile" />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <RadioProvider>
        <AppContent />
      </RadioProvider>
    </LanguageProvider>
  );
}

export default App;